// src/index.ts
import { QueryClient } from "@tanstack/react-query";
import { ApiClient, apiClient, handleApiError } from "@tasama/api-client";
import {
  QueryClientProvider,
  useQueryClient as useQueryClient2,
  useQuery as useQuery2
} from "@tanstack/react-query";

// src/hooks.ts
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery
} from "@tanstack/react-query";
var useApiQuery = (key, endpoint, config) => {
  const method = config?.method || "GET";
  const requestBody = config?.requestBody;
  return useQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: async () => {
      try {
        let response;
        if (method === "POST") {
          response = await apiClient.axios.post(
            endpoint,
            requestBody,
            config?.axiosConfig
          );
        } else {
          response = await apiClient.axios.get(
            endpoint,
            config?.axiosConfig
          );
        }
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    ...config
  });
};
var useApiMutation = (endpoint, method = "POST", config) => {
  const queryClient2 = useQueryClient();
  return useMutation({
    ...config,
    mutationFn: async (data) => {
      const resolvedEndpoint = typeof endpoint === "function" ? endpoint(data) : endpoint;
      let response;
      switch (method) {
        case "GET":
          response = await apiClient.axios.get(resolvedEndpoint);
          break;
        case "POST":
          response = await apiClient.axios.post(resolvedEndpoint, data);
          break;
        case "PUT":
          response = await apiClient.axios.put(resolvedEndpoint, data);
          break;
        case "PATCH":
          response = await apiClient.axios.patch(resolvedEndpoint, data);
          break;
        case "DELETE":
          response = await apiClient.axios.delete(resolvedEndpoint);
          break;
      }
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      config?.invalidateKeys?.forEach((key) => {
        queryClient2.invalidateQueries({ queryKey: [key] });
      });
      config?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const apiError = handleApiError(error);
      config?.onError?.(apiError, variables, context);
    }
  });
};
var useFileUpload = (endpoint, config) => {
  const queryClient2 = useQueryClient();
  const mutation = useMutation({
    ...config,
    mutationFn: async (formData) => {
      const resolvedEndpoint = typeof endpoint === "function" ? endpoint(formData) : endpoint;
      const axiosMethod = config?.method === "PUT" ? "put" : "post";
      const response = await apiClient.axios[axiosMethod](
        resolvedEndpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total ? Math.round(progressEvent.loaded * 100 / progressEvent.total) : 0;
            config?.onProgress?.(progress);
          }
        }
      );
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      config?.invalidateKeys?.forEach((key) => {
        queryClient2.invalidateQueries({ queryKey: [key] });
      });
      config?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const apiError = handleApiError(error);
      config?.onError?.(apiError, variables, context);
    }
  });
  const mutateAsync = async (formData) => {
    if (!navigator.onLine) {
      const offlineError = {
        message: "Network unavailable. Please check your connection.",
        code: "NETWORK_OFFLINE"
      };
      config?.onError?.(offlineError, formData, void 0);
      throw new Error(offlineError.message);
    }
    return mutation.mutateAsync(formData);
  };
  return { ...mutation, mutateAsync };
};
var useInfiniteApiQuery = (key, config) => {
  const baseOptions = {
    queryKey: key,
    queryFn: async ({ pageParam }) => {
      const endpoint = config.getEndpoint(pageParam);
      const response = await apiClient.axios.get(endpoint);
      if (config.select) {
        return config.select(response.data);
      }
      return response.data;
    },
    initialPageParam: config.initialPageParam ?? 0,
    getNextPageParam: config.getNextPageParam
  };
  const finalOptions = { ...baseOptions };
  if (config.staleTime !== void 0) {
    finalOptions.staleTime = config.staleTime;
  }
  if (config.retry !== void 0) {
    finalOptions.retry = config.retry;
  }
  if (config.retryDelay !== void 0) {
    finalOptions.retryDelay = config.retryDelay;
  }
  if (config.enabled !== void 0) {
    finalOptions.enabled = config.enabled;
  }
  if (config.refetchOnMount !== void 0) {
    finalOptions.refetchOnMount = config.refetchOnMount;
  }
  return useInfiniteQuery(finalOptions);
};

// src/index.ts
var createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1e3
        // 5 minutes
      },
      mutations: {
        retry: 0
      }
    }
  });
};
var queryClient = createQueryClient();
export {
  ApiClient,
  QueryClientProvider,
  apiClient,
  createQueryClient,
  handleApiError,
  queryClient,
  useApiMutation,
  useApiQuery,
  useFileUpload,
  useInfiniteApiQuery,
  useQuery2 as useQuery,
  useQueryClient2 as useQueryClient
};
//# sourceMappingURL=index.js.map